import React from 'react';
import { Home, Bath, Square, MapPin, Building, Calendar, Phone, Mail, Check, ExternalLink } from 'lucide-react';
import Modal from '../property/Modal';
import { QRCodeSVG } from 'qrcode.react';
import ProfileImage from '../property/ProfileImage';

// URLs de Cloudflare
const CLOUDFLARE_PROPERTIES_URL = 'https://pub-e51257e7234a40ea96269a0ac4d1c6ab.r2.dev';

// Componente para imágenes de propiedades
const PropertyImage = ({ url, alt, className }) => {
  if (!url) return (
    <img
      src="/default-property-image.jpg"
      alt={alt}
      className={className}
    />
  );

  const fullUrl = url.startsWith('http') ? url : `${CLOUDFLARE_PROPERTIES_URL}/${url}`;

  return (
    <img
      src={fullUrl}
      alt={alt}
      className={className}
      onError={(e) => {
        console.error('Error loading property image:', fullUrl);
        e.target.src = '/default-property-image.jpg';
      }}
    />
  );
};

const RealEstateListing = ({ property }) => {
  // Asegurar que siempre tengamos un objeto vendedor válido
  const vendedor = property?.vendedor || {
    nombre: 'Francisco Alvarado',
    foto_perfil: null,
    tipo_usuario_id: 3,
    email_verificado: true,
    telefono_verificado: true,
    ubicacion: 'San Miguel, Provincia de Lima, Perú',
    telefono: '+51 987 654 321',
    email: 'francisco@inkayros.com',
    nombre_usuario: 'francisco.alvarado'
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-PE', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  // Generar URL del QR basado en el tipo de operación
  const getPropertyUrl = () => {
    const baseUrl = 'https://inkayros.com';
    const path = property.operacion === 'alquiler' ? 'alquiler' : 'propiedad';
    return `${baseUrl}/${path}/${property.id}`;
  };

  const tipoUsuario = {
    1: 'Cliente',
    2: 'Vendedor',
    3: 'Constructora',
    4: 'Inmobiliaria'
  }[vendedor.tipo_usuario_id] || 'Usuario';

  // Características principales
  const mainFeatures = [
    { icon: Home, label: 'Dormitorios', value: property.habitaciones },
    { icon: Bath, label: 'Baños', value: property.banos },
    { icon: Square, label: 'Área Total', value: `${property.area_total}m²` }
  ];

  // Estilo fijo y no adaptable para evitar deformaciones
  return (
    <div className="overflow-hidden shadow-2xl bg-white" style={{
      width: '600px',
      height: 'auto',
      display: 'block',
      position: 'relative',
      borderRadius: '16px',
      margin: '0 auto'
    }}>
      {/* Property Type Banner - Posición fija */}
      <div className="relative">
        <div className="absolute top-4 left-4 z-20">
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-4 py-1.5 rounded-full text-sm font-medium shadow-lg" style={{
            zIndex: 10,
            position: 'absolute',
            top: '16px',
            left: '16px',
            width: 'auto',
            whiteSpace: 'nowrap',
            textAlign: 'center'
          }}>
            {property.operacion === 'alquiler' ? 'EN ALQUILER' : 'EN VENTA'}
          </div>
        </div>
        
        {/* Main Image with Gradient Overlay */}
        <div style={{ position: 'relative', height: '288px', width: '100%' }}>
          <PropertyImage 
            url={property.imagenes[0]}
            alt={property.titulo}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-transparent to-black/70"></div>
          
          {/* QR Code in top right */}
          <div style={{
            position: 'absolute',
            top: '16px',
            right: '16px',
            zIndex: 20,
            backgroundColor: 'white',
            padding: '8px',
            borderRadius: '8px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
          }}>
            <QRCodeSVG 
              value={getPropertyUrl()} 
              size={70} 
              level="M"
              includeMargin={false}
            />
          </div>
          
          {/* Title Overlay - Fijo en la parte inferior */}
          <div style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            padding: '20px',
            zIndex: 10
          }}>
            <h1 className="text-2xl font-bold text-white leading-tight mb-2">
              {property.titulo}
            </h1>
            <div className="flex items-center text-white/90 gap-1.5">
              <MapPin className="w-4 h-4" />
              <span className="text-sm truncate">{property.ubicacion || vendedor.ubicacion}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Price Section - Estructura fija */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-4 flex justify-between items-center">
        <div>
          <div className="text-blue-100 text-xs font-medium mb-1">PRECIO</div>
          <div className="text-white text-2xl font-bold mb-1">S/ {formatPrice(property.precio_soles)}</div>
          <div className="text-blue-100 text-sm">USD {formatPrice(property.precio_dolares)}</div>
        </div>
        
        {/* Action Button */}
        <button className="bg-white text-blue-700 px-4 py-2 rounded-lg shadow-md font-medium flex items-center gap-2 hover:bg-blue-50 transition-colors">
          <Calendar className="w-4 h-4" />
          <span>Agendar visita</span>
        </button>
      </div>

      {/* Main Content - Con estructura fija */}
      <div className="p-5">
        {/* Features Section */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          {mainFeatures.map((Feature, index) => (
            <div key={index} className="bg-blue-50 rounded-lg p-3 text-center">
              <Feature.icon className="w-6 h-6 text-blue-600 mx-auto mb-2" />
              <div className="text-xl font-bold text-gray-900">{Feature.value}</div>
              <div className="text-xs text-gray-600">{Feature.label}</div>
            </div>
          ))}
        </div>
        
        {/* Description */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-2">Descripción</h3>
          <p className="text-gray-600 text-sm">
            {property.descripcion_corta || 
             'Hermosa propiedad con acabados de primera calidad, ubicada en una zona estratégica con acceso a servicios y vías principales, ideal para familias que buscan confort y seguridad.'}
          </p>
        </div>
        
        {/* Agent Information */}
        <div className="border-t border-gray-100 pt-4">
          <div className="flex items-center gap-4 mb-4">
            <ProfileImage 
              url={vendedor.foto_perfil}
              alt={vendedor.nombre}
              className="w-12 h-12 rounded-full object-cover border-2 border-blue-100"
            />
            <div>
              <div className="text-gray-900 font-medium">{vendedor.nombre}</div>
              <div className="text-sm text-gray-500 flex items-center gap-1.5">
                <Building className="w-3.5 h-3.5 text-blue-600" />
                <span>{tipoUsuario}</span>
                {(vendedor.email_verificado && vendedor.telefono_verificado) && (
                  <span className="text-green-600 flex items-center gap-0.5">
                    <Check className="w-3.5 h-3.5" />
                    Verificado
                  </span>
                )}
              </div>
            </div>
          </div>
          
          {/* Contact Information */}
          <div className="grid grid-cols-2 gap-3">
            <div className="flex items-center gap-2">
              <div className="bg-blue-100 p-2 rounded-full">
                <Phone className="w-4 h-4 text-blue-600" />
              </div>
              <div>
                <div className="text-xs text-gray-500">Teléfono</div>
                <div className="text-sm text-gray-900">{property.telefono || vendedor.telefono || '+51 123 456 789'}</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="bg-blue-100 p-2 rounded-full">
                <Mail className="w-4 h-4 text-blue-600" />
              </div>
              <div>
                <div className="text-xs text-gray-500">Email</div>
                <div className="text-sm text-gray-900">{property.email || vendedor.email || 'contacto@inkayros.com'}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-gray-50 py-3 px-5 text-center">
        <div className="text-sm text-gray-600">
          www.InKayros.com © {new Date().getFullYear()}
        </div>
      </div>
    </div>
  );
};

// Preview component con el modal
export const RealEstateListingPreview = ({ isOpen, onClose, property }) => {
  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="max-w-4xl mx-auto">
        <RealEstateListing property={property} />
        <button
          onClick={onClose}
          className="mt-6 w-full bg-gradient-to-r from-blue-700 to-blue-900 
                   text-white py-4 px-6 rounded-xl font-semibold 
                   shadow-lg hover:shadow-xl transition-all duration-300 
                   hover:scale-105 active:scale-100"
        >
          Cerrar
        </button>
      </div>
    </Modal>
  );
};

export default RealEstateListing;